import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { APP_FONT_FAMILIES } from '@/lib/design/typography';
import * as Haptics from 'expo-haptics';
import { FileImageIcon, GripHorizontalIcon, PlusIcon } from 'lucide-react-native';
import * as React from 'react';
import {
  ActionSheetIOS,
  Alert,
  ActivityIndicator,
  Platform,
  Pressable,
  View,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Easing,
  runOnJS,
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { PosterFocalImage } from './PosterFocalImage';
import type { DraftPoster } from './types';
import { clampInteger } from './utils';

const SMALL_SLOT_COUNT = 4;
const SLOT_GAP = 8;
const COVER_RADIUS = 24;
const SMALL_RADIUS = 16;
const MOVE = { duration: 200, easing: Easing.out(Easing.cubic) } as const;
const FAST = { duration: 120, easing: Easing.out(Easing.quad) } as const;
const INSTANT = { duration: 80, easing: Easing.linear } as const;
const DASHED_COLOR = 'rgba(255,255,255,0.15)';

type SlotFrame = {
  left: number;
  top: number;
  width: number;
  height: number;
  radius: number;
};

const EMPTY_SLOT_FRAME: SlotFrame = {
  left: 0,
  top: 0,
  width: 0,
  height: 0,
  radius: SMALL_RADIUS,
};

function getSlotFrame(frames: SlotFrame[], index: number): SlotFrame {
  'worklet';
  return frames[index] ?? frames[0] ?? EMPTY_SLOT_FRAME;
}

function triggerHaptic() {
  Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
}

function buildSlotFrames(laneWidth: number) {
  const coverWidth = laneWidth;
  const coverHeight = coverWidth * (5 / 4);
  const smallWidth = (laneWidth - SLOT_GAP * (SMALL_SLOT_COUNT - 1)) / SMALL_SLOT_COUNT;
  const smallHeight = smallWidth * (5 / 4);
  const top = coverHeight + SLOT_GAP;

  return Array.from({ length: SMALL_SLOT_COUNT + 1 }, (_, index) => {
    if (index === 0) {
      return {
        left: 0,
        top: 0,
        width: coverWidth,
        height: coverHeight,
        radius: COVER_RADIUS,
      } satisfies SlotFrame;
    }

    return {
      left: (index - 1) * (smallWidth + SLOT_GAP),
      top,
      width: smallWidth,
      height: smallHeight,
      radius: SMALL_RADIUS,
    } satisfies SlotFrame;
  });
}

type PosterCardProps = {
  poster: DraftPoster;
  radius: number;
  onPress: () => void;
  onRemove: () => void;
  onRetry: () => void;
  showCoverBadge: boolean;
  isReorderMode: boolean;
};

function PosterCard({
  poster,
  radius,
  onPress,
  onRemove,
  onRetry,
  showCoverBadge,
  isReorderMode,
}: PosterCardProps) {
  return (
    <Pressable
      onPress={onPress}
      disabled={isReorderMode}
      delayLongPress={150}
      unstable_pressDelay={120}>
      <View
        style={{
          height: '100%',
          overflow: 'hidden',
          borderRadius: radius,
          borderWidth: isReorderMode ? 1.5 : 1,
          borderColor: isReorderMode
            ? DESIGN_TOKENS.colors.accent
            : poster.status === 'failed'
              ? 'rgba(239,106,61,0.48)'
              : 'rgba(255,255,255,0.12)',
          backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
        }}>
        <PosterFocalImage poster={poster} borderRadius={radius} />

        {showCoverBadge ? (
          <View
            pointerEvents="none"
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: 'rgba(0,0,0,0.55)',
              borderRadius: 999,
              paddingHorizontal: 8,
              paddingVertical: 3,
            }}>
            <Text variant="small" style={{ color: '#ffffff', fontSize: 10, fontWeight: '700' }}>
              Cover
            </Text>
          </View>
        ) : null}

        <Pressable
          onPress={onRemove}
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 22,
            height: 22,
            borderRadius: 11,
            backgroundColor: 'rgba(0,0,0,0.65)',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Text variant="small" style={{ color: '#ffffff', fontSize: 14, lineHeight: 16 }}>
            ×
          </Text>
        </Pressable>

        {poster.status === 'uploading' ? (
          <View
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.44)',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}>
            <ActivityIndicator color="#ffffff" size="small" />
            <Text variant="small" style={{ color: '#ffffff', fontSize: 11 }}>
              {showCoverBadge ? 'Uploading cover...' : `${Math.round(poster.progress * 100)}%`}
            </Text>
          </View>
        ) : null}

        {poster.status === 'failed' ? (
          <View
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(239,78,46,0.2)',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}>
            <Text variant="small" style={{ color: '#ffffff', fontWeight: '600' }}>
              Upload failed
            </Text>
            <Pressable
              onPress={onRetry}
              style={{
                borderRadius: 12,
                backgroundColor: 'rgba(239,106,61,0.92)',
                paddingHorizontal: 16,
                paddingVertical: 8,
              }}>
              <Text variant="small" style={{ color: '#ffffff', fontWeight: '700' }}>
                Retry
              </Text>
            </Pressable>
          </View>
        ) : null}
      </View>
    </Pressable>
  );
}

type EmptyPosterSlotProps = {
  frame: SlotFrame;
  isCover: boolean;
  isImportingFlyer: boolean;
  onAddPoster: () => void;
  isReorderMode: boolean;
};

function EmptyPosterSlot({
  frame,
  isCover,
  isImportingFlyer,
  onAddPoster,
  isReorderMode,
}: EmptyPosterSlotProps) {
  return (
    <Pressable
      onPress={onAddPoster}
      disabled={isImportingFlyer || isReorderMode}
      delayLongPress={100}
      unstable_pressDelay={80}
      style={{
        position: 'absolute',
        left: frame.left,
        top: frame.top,
        width: frame.width,
        height: frame.height,
        borderRadius: frame.radius,
        borderWidth: isReorderMode ? 1.5 : 1.5,
        borderStyle: 'dashed',
        borderColor: isReorderMode ? DESIGN_TOKENS.colors.accent : DASHED_COLOR,
        backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
        alignItems: 'center',
        justifyContent: 'center',
        gap: isCover ? 10 : 6,
        opacity: isImportingFlyer ? 0.6 : 1,
      }}>
      <Icon
        as={isCover ? FileImageIcon : PlusIcon}
        size={isCover ? 32 : 20}
        style={{ color: DESIGN_TOKENS.colors.textSoft }}
      />
      {isCover ? (
        <>
          <Text variant="default" style={{ fontWeight: '600', color: DESIGN_TOKENS.colors.text }}>
            Add flyer
          </Text>
          <Text
            variant="small"
            style={{
              color: DESIGN_TOKENS.colors.textSoft,
              textAlign: 'center',
              paddingHorizontal: 32,
            }}>
            Cover image for discovery and event detail
          </Text>
        </>
      ) : null}
    </Pressable>
  );
}

function DraggablePosterSlot({
  poster,
  frames,
  coverFrame,
  smallFrameBase,
  positions,
  activeCount,
  smallFrame,
  draggingPosterID,
  setDraggingPosterID,
  onRemovePoster,
  onRetryPoster,
  onOpenPosterCrop,
  onOpenCoverMenu,
  onReorderPosters,
  isReorderMode,
}: {
  poster: DraftPoster;
  frames: SharedValue<SlotFrame[]>;
  coverFrame: SlotFrame;
  smallFrameBase: Pick<SlotFrame, 'width' | 'height'>;
  positions: SharedValue<Record<string, number>>;
  activeCount: SharedValue<number>;
  smallFrame: SharedValue<Pick<SlotFrame, 'width' | 'height' | 'radius'>>;
  draggingPosterID: string | null;
  setDraggingPosterID: React.Dispatch<React.SetStateAction<string | null>>;
  onRemovePoster: (localID: string) => void;
  onRetryPoster: (localID: string) => void;
  onOpenPosterCrop: (localID: string) => void;
  onOpenCoverMenu: (poster: DraftPoster) => void;
  onReorderPosters: (nextOrder: string[]) => void;
  isReorderMode: boolean;
}) {
  const dragPreviewX = useSharedValue(0);
  const dragPreviewY = useSharedValue(0);
  const dragStartX = useSharedValue(0);
  const dragStartY = useSharedValue(0);
  const isDragging = useSharedValue(false);
  const dragScale = useSharedValue(1);
  const reorderModeOpacity = useSharedValue(isReorderMode ? 1 : 0);

  React.useEffect(() => {
    reorderModeOpacity.value = withTiming(isReorderMode ? 1 : 0, FAST);
  }, [isReorderMode, reorderModeOpacity]);

  const commitOrder = React.useCallback(
    (nextPositions: Record<string, number>) => {
      const nextOrder = Object.entries(nextPositions)
        .sort((a, b) => a[1] - b[1])
        .map(([localID]) => localID);
      onReorderPosters(nextOrder);
    },
    [onReorderPosters],
  );

  const gesture = React.useMemo(
    () =>
      Gesture.Pan()
        .activateAfterLongPress(300)
        .enabled(isReorderMode && activeCount.value > 1)
        .runOnJS(false)
        .onStart(() => {
          const slotIndex = positions.value[poster.localID] ?? 0;
          const frame = getSlotFrame(frames.value, slotIndex);
          const previewWidth = smallFrame.value.width;
          const previewHeight = smallFrame.value.height;
          dragStartX.value = frame.left + (frame.width - previewWidth) / 2;
          dragStartY.value = frame.top + (frame.height - previewHeight) / 2;
          dragPreviewX.value = dragStartX.value;
          dragPreviewY.value = dragStartY.value;
          isDragging.value = true;
          dragScale.value = withTiming(1.03, INSTANT);
          runOnJS(setDraggingPosterID)(poster.localID);
          runOnJS(triggerHaptic)();
        })
        .onUpdate((event) => {
          const nextX = dragStartX.value + event.translationX;
          const nextY = dragStartY.value + event.translationY;
          dragPreviewX.value = nextX;
          dragPreviewY.value = nextY;

          const centerX = nextX + smallFrame.value.width / 2;
          const centerY = nextY + smallFrame.value.height / 2;
          const currentIndex = positions.value[poster.localID] ?? 0;
          const maxIndex = Math.max(0, activeCount.value - 1);
          let nextIndex = currentIndex;
          let nearestDistance = Number.POSITIVE_INFINITY;

          for (let index = 0; index <= maxIndex; index += 1) {
            const frame = getSlotFrame(frames.value, index);
            const frameCenterX = frame.left + frame.width / 2;
            const frameCenterY = frame.top + frame.height / 2;
            const distance = Math.hypot(centerX - frameCenterX, centerY - frameCenterY);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nextIndex = index;
            }
          }

          nextIndex = clampInteger(nextIndex, 0, maxIndex);
          if (nextIndex === currentIndex) return;

          const nextPositions = { ...positions.value };
          Object.keys(nextPositions).forEach((localID) => {
            if (localID === poster.localID) return;
            if (nextPositions[localID] === nextIndex) {
              nextPositions[localID] = currentIndex;
            }
          });
          nextPositions[poster.localID] = nextIndex;
          positions.value = nextPositions;
        })
        .onFinalize(() => {
          isDragging.value = false;
          dragScale.value = withTiming(1, FAST);
          runOnJS(setDraggingPosterID)(null);
          runOnJS(commitOrder)(positions.value);
        }),
    [
      activeCount,
      commitOrder,
      dragPreviewX,
      dragPreviewY,
      dragStartX,
      dragStartY,
      frames,
      dragScale,
      isDragging,
      positions,
      poster,
      setDraggingPosterID,
      smallFrame,
      isReorderMode,
    ],
  );

  const slotBaseStyle = React.useMemo(
    () => ({
      position: 'absolute' as const,
      left: 0,
      top: 0,
      width: coverFrame.width,
      height: coverFrame.height,
      transformOrigin: 'top left' as const,
    }),
    [coverFrame.height, coverFrame.width],
  );

  const previewBaseStyle = React.useMemo(
    () => ({
      position: 'absolute' as const,
      left: 0,
      top: 0,
      width: smallFrameBase.width * 1.8,
      height: smallFrameBase.height * 1.8,
      transformOrigin: 'top left' as const,
    }),
    [smallFrameBase.height, smallFrameBase.width],
  );

  const slotStyle = useAnimatedStyle(() => {
    const slotIndex = positions.value[poster.localID] ?? 0;
    const frame = getSlotFrame(frames.value, slotIndex);
    const scaleX = coverFrame.width > 0 ? frame.width / coverFrame.width : 1;
    const scaleY = coverFrame.height > 0 ? frame.height / coverFrame.height : 1;
    return {
      opacity: withTiming(isDragging.value ? 0.25 : 1, isDragging.value ? INSTANT : FAST),
      zIndex: 2,
      transform: [
        { translateX: withTiming(frame.left, MOVE) },
        { translateY: withTiming(frame.top, MOVE) },
        { scaleX: withTiming(scaleX, MOVE) },
        { scaleY: withTiming(scaleY, MOVE) },
      ],
    };
  }, [coverFrame.height, coverFrame.width]);

  const previewStyle = useAnimatedStyle(() => {
    const previewW = smallFrame.value.width * 1.8;
    const previewH = smallFrame.value.height * 1.8;
    return {
      opacity: withTiming(isDragging.value ? 1 : 0, INSTANT),
      zIndex: 50,
      transform: [
        { translateX: dragPreviewX.value - (previewW - smallFrame.value.width) / 2 },
        { translateY: dragPreviewY.value - (previewH - smallFrame.value.height) / 2 },
        { scale: dragScale.value },
      ],
    };
  });

  const reorderBorderStyle = useAnimatedStyle(() => ({
    opacity: reorderModeOpacity.value,
  }));

  const slotIndex = positions.value[poster.localID] ?? 0;
  const isCover = slotIndex === 0;
  const handlePress = () => {
    if (isReorderMode || draggingPosterID) return;
    if (poster.status === 'failed') {
      onRetryPoster(poster.localID);
      return;
    }
    if (poster.status !== 'uploaded') {
      return;
    }
    if ((positions.value[poster.localID] ?? 0) === 0) {
      onOpenCoverMenu(poster);
      return;
    }
    onOpenPosterCrop(poster.localID);
  };

  const slotContent = (
    <Animated.View style={[slotBaseStyle, slotStyle]}>
      <PosterCard
        poster={poster}
        radius={isCover ? COVER_RADIUS : SMALL_RADIUS}
        onPress={handlePress}
        onRemove={() => onRemovePoster(poster.localID)}
        onRetry={() => onRetryPoster(poster.localID)}
        showCoverBadge={isCover}
        isReorderMode={isReorderMode}
      />

      <Animated.View
        pointerEvents="none"
        style={[
          {
            position: 'absolute',
            inset: 0,
            borderRadius: isCover ? COVER_RADIUS : SMALL_RADIUS,
            borderWidth: 1.5,
            borderColor: DESIGN_TOKENS.colors.accent,
          },
          reorderBorderStyle,
        ]}
      />

      {isReorderMode ? (
        <View
          pointerEvents="none"
          style={{
            position: 'absolute',
            bottom: 8,
            alignSelf: 'center',
            padding: 8,
            zIndex: 20,
            opacity: 0.5,
          }}>
          <Icon as={GripHorizontalIcon} size={14} color="rgba(255,255,255,0.5)" />
        </View>
      ) : null}
    </Animated.View>
  );

  return (
    <>
      {isReorderMode ? <GestureDetector gesture={gesture}>{slotContent}</GestureDetector> : slotContent}

      <Animated.View pointerEvents="none" style={[previewBaseStyle, previewStyle]}>
        <PosterCard
          poster={poster}
          radius={SMALL_RADIUS}
          onPress={() => undefined}
          onRemove={() => undefined}
          onRetry={() => undefined}
          showCoverBadge={false}
          isReorderMode={false}
        />
      </Animated.View>
    </>
  );
}

export function PosterThumbnailStrip({
  posters,
  posterLimit,
  isImportingFlyer,
  onAddPoster,
  onRemovePoster,
  onRetryPoster,
  onOpenPosterCrop,
  onReorderPosters,
}: {
  posters: DraftPoster[];
  posterLimit: number;
  isImportingFlyer: boolean;
  onAddPoster: () => void;
  onRemovePoster: (localID: string) => void;
  onRetryPoster: (localID: string) => void;
  onOpenPosterCrop: (localID: string) => void;
  onReorderPosters: (nextOrder: string[]) => void;
}) {
  const [laneWidth, setLaneWidth] = React.useState(0);
  const [draggingPosterID, setDraggingPosterID] = React.useState<string | null>(null);
  const [isReorderMode, setIsReorderMode] = React.useState(false);
  const frames = React.useMemo(() => (laneWidth > 0 ? buildSlotFrames(laneWidth) : []), [laneWidth]);
  const framesShared = useSharedValue<SlotFrame[]>([]);
  const positions = useSharedValue<Record<string, number>>({});
  const activeCount = useSharedValue(posters.length);
  const smallFrame = useSharedValue({ width: 0, height: 0, radius: SMALL_RADIUS });

  React.useEffect(() => {
    framesShared.value = frames;
    if (frames[1]) {
      smallFrame.value = {
        width: frames[1].width,
        height: frames[1].height,
        radius: SMALL_RADIUS,
      };
    }
  }, [frames, framesShared, smallFrame]);

  React.useEffect(() => {
    activeCount.value = posters.length;
    positions.value = Object.fromEntries(posters.map((poster, index) => [poster.localID, index]));
  }, [activeCount, positions, posters]);

  React.useEffect(() => {
    if (posters.length <= 1) {
      setIsReorderMode(false);
    }
  }, [posters.length]);

  const coverHeight = frames[0]?.height ?? 0;
  const smallHeight = frames[1]?.height ?? 0;
  const emptyCoverHeight = laneWidth > 0 ? laneWidth * (3 / 4) : 0;
  const totalHeight =
    laneWidth > 0
      ? posters.length > 0
        ? coverHeight + SLOT_GAP + smallHeight
        : emptyCoverHeight
      : 0;

  const openCoverMenu = React.useCallback(
    (poster: DraftPoster) => {
      if (Platform.OS === 'ios') {
        ActionSheetIOS.showActionSheetWithOptions(
          {
            options: ['Crop', 'Replace image', 'Cancel'],
            cancelButtonIndex: 2,
          },
          (selectedIndex) => {
            if (selectedIndex === 0) onOpenPosterCrop(poster.localID);
            if (selectedIndex === 1) onAddPoster();
          },
        );
        return;
      }

      Alert.alert('Cover image', undefined, [
        { text: 'Crop', onPress: () => onOpenPosterCrop(poster.localID) },
        { text: 'Replace image', onPress: onAddPoster },
        { text: 'Cancel', style: 'cancel' },
      ]);
    },
    [onAddPoster, onOpenPosterCrop],
  );

  return (
    <View style={{ gap: 12 }}>
      <View
        onLayout={(event) => setLaneWidth(event.nativeEvent.layout.width)}
        style={totalHeight > 0 ? { height: totalHeight, position: 'relative' } : { minHeight: 180 }}>
        {laneWidth > 0
          ? posters.map((poster) => (
              <DraggablePosterSlot
                key={poster.localID}
                poster={poster}
                frames={framesShared}
                coverFrame={frames[0]}
                smallFrameBase={frames[1] ?? frames[0]}
                positions={positions}
                activeCount={activeCount}
                smallFrame={smallFrame}
                draggingPosterID={draggingPosterID}
                setDraggingPosterID={setDraggingPosterID}
                onRemovePoster={onRemovePoster}
                onRetryPoster={onRetryPoster}
                onOpenPosterCrop={onOpenPosterCrop}
                onOpenCoverMenu={openCoverMenu}
                onReorderPosters={onReorderPosters}
                isReorderMode={isReorderMode}
              />
            ))
          : null}

        {laneWidth > 0 && posters.length === 0 && frames[0]
          ? (
              <EmptyPosterSlot
                frame={{ ...frames[0], height: emptyCoverHeight }}
                isCover
                isImportingFlyer={isImportingFlyer}
                onAddPoster={onAddPoster}
                isReorderMode={isReorderMode}
              />
            )
          : null}

        {laneWidth > 0 && posters.length > 0
          ? Array.from({
              length: Math.max(0, Math.min(posterLimit, SMALL_SLOT_COUNT + 1) - Math.max(1, posters.length)),
            }).map((_, index) => {
              const slotIndex = Math.max(1, posters.length) + index;
              const frame = frames[slotIndex];
              if (!frame || slotIndex === 0) return null;
              return (
                <EmptyPosterSlot
                  key={`empty-${slotIndex}`}
                  frame={frame}
                  isCover={false}
                  isImportingFlyer={isImportingFlyer}
                  onAddPoster={onAddPoster}
                  isReorderMode={isReorderMode}
                />
              );
            })
          : null}
      </View>

      {posters.length > 1 ? (
        <Pressable
          onPress={() => setIsReorderMode((current) => !current)}
          hitSlop={8}
          style={{
            paddingVertical: 8,
            paddingHorizontal: 16,
            alignSelf: 'flex-start',
          }}>
          <Text
            variant="small"
            style={{
              fontSize: 13,
              color: isReorderMode
                ? DESIGN_TOKENS.colors.accent
                : DESIGN_TOKENS.colors.textMuted,
              fontFamily: APP_FONT_FAMILIES.bodySemiBold,
            }}>
            {isReorderMode ? 'Done' : 'Reorder'}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}
